/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useContext } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// Language context（依語言切換 logo）
import { LanguageContext } from "../../context/LanguageContext";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const currentPath = location.pathname;
  const { language } = useContext(LanguageContext);

  // 2026-05-14 logo 規則：
  //   - 收合（miniSidenav）→ logo.png
  //   - 展開 + 中文（zh-TW / zh-CN）→ logo_1_1.png
  //   - 展開 + 其他語言 → logo_1_2.png
  const brandLogoSrc = miniSidenav
    ? "/images/logo/logo.png"
    : language === "zh-TW" || language === "zh-CN"
    ? "/images/logo/logo_1_1.png"
    : "/images/logo/logo_1_2.png";

  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 2026-06-08 收合 sidebar 時，把所有展開的下拉群組一併收起，
  //   否則 mini 模式下 <Collapse in> 仍會渲染子項，排版會跑掉（Sean 回報）
  useEffect(() => {
    if (miniSidenav) setOpenGroups({});
  }, [miniSidenav]);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      // 2026-05-25 修 resize bug：原本 < 1200 會把 whiteSidenav 設 false → sidebar 變深色漸層底
      // 本站一律白底（除非 darkMode），改成永遠保持白
      setWhiteSidenav(dispatch, !darkMode);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav, darkMode]);

  // 2026-05-25 判斷某個 route 是否命中當前路徑（給 parent「是否含 active 子頁」用）
  const pathMatches = (r) => {
    const clean = (r || "").replace("/*", "");
    if (!clean) return false;
    return currentPath === clean || currentPath.startsWith(clean + "/");
  };
  // 遞迴判斷一組 children 內是否有任何一個命中當前路徑（含 matchPaths / 巢狀 children）
  const containsActivePath = (items) =>
    (items || []).some(
      (it) =>
        pathMatches(it.route) ||
        (Array.isArray(it.matchPaths) && it.matchPaths.some(pathMatches)) ||
        containsActivePath(it.children)
    );

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = (allRoutes, isChild = false) =>
    allRoutes
      .filter(({ name, route, hidden }) => {
        // Explicitly exclude unwanted routes
        if (hidden || !name || name === "Admin Login" || (route && route.includes("sign-in"))) return false;
        return true;
      })
      .map((routeItem) => {
      const { type = "collapse", name, icon, title, noCollapse, key, href, route, sidenavRoute, external, children, matchPaths } = routeItem;
      // sidenavRoute 給「menu 點擊要去的路徑」覆寫用 (例：/mas-genie?force=true)
      // matchPaths 額外列出其他要 highlight 此項的路徑（例：在 /my-genie-strategies 時策略精靈也亮）
      // active 狀態比對還是用乾淨的 route (不含 query) 比對 currentPath
      const navTarget = sidenavRoute || route;
      const isMatch = (r) => {
        const clean = (r || '').replace('/*', '');
        if (!clean) return false;
        return currentPath === clean || currentPath.startsWith(clean + '/');
      };
      const isActiveRoute = isMatch(route) || (Array.isArray(matchPaths) && matchPaths.some(isMatch));
      let returnValue;

      if (type === "collapse") {
        if (children) {
          // Parent item
          const isOpen = openGroups[key] || false;
          // 2026-05-25 父群組只在「包含當前頁面」時 highlight，不再因展開就亮
          const parentActive = containsActivePath(children);
          returnValue = (
            <div key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                active={parentActive}
                noCollapse={noCollapse}
                onClick={() => toggleGroup(key)}
                isParent
                isOpen={isOpen}
              />
              {/* 2026-06-08 mini 且非 hover（miniSidenav=true）時不渲染子項，
                  否則無 icon 的子項會在收合側欄留下空白列（Sean 回報英文版 gap）。
                  App.js 用 hover 切換 miniSidenav state，故 hover 時 miniSidenav=false 子項照常展開可點 */}
              <Collapse in={isOpen && !miniSidenav} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {renderRoutes(children, true)}
                </List>
              </Collapse>
            </div>
          );
        } else {
          // Leaf item
          const isExternal = external && route.startsWith("http");
          returnValue = isExternal ? (
            <SidenavCollapse
              key={key}
              component="a"
              href={route}
              target="_blank"
              rel="noopener noreferrer"
              name={name}
              icon={icon}
              active={false}
              noCollapse={noCollapse}
              isChild={isChild}
            />
          ) : (
            <SidenavCollapse
              key={key}
              component={NavLink}
              to={navTarget}
              name={name}
              icon={icon}
              active={isActiveRoute}
              isChild={isChild}
            />
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={1.5} pb={1} px={miniSidenav ? 1.25 : 4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <MDBox
            component="img"
            src={brandLogoSrc}
            alt="Brand"
            width="100%"
            maxWidth={miniSidenav ? "46px" : "189px"}
            sx={{ my: 1, transition: "max-width 0.3s ease" }}
          />
        </MDBox>
      </MDBox>
      <List sx={{ pt: 0 }}>{renderRoutes(routes)}</List>
      {/* <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          href="https://www.creative-tim.com/product/material-dashboard-pro-react"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth
        >
          upgrade to pro
        </MDButton>
      </MDBox> */}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
